/**
 * แผนงบรายเดือน: the current month plus the next 2, paging back 3 at a time.
 * A closed month is locked (🔒) for everyone but the master, who overrides it
 * with a reason. A missing plan reads "—", never 0.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { monthStartOf, nextMonthStart } from '@/services/dataDate';
import { planFor, TARGET_PLATFORMS, type PlanRow, type TargetPlatform } from '@/services/targets/targetRules';
import { overrideLockedPlan, proposePlan, withdrawProposal } from '@/services/targets/targetsService';
import { TargetCell, type CellSaveInput } from './TargetCell';
import { keyPlatformLabel, money, shiftMonth, type TargetRole } from './targetUi';

interface Props {
  workspaceId: string;
  plans: readonly PlanRow[];
  currency: string;
  today: string;
  role: TargetRole;
  me: string | null;
  reload: () => void;
}

const COLUMNS: readonly (TargetPlatform | null)[] = [null, ...TARGET_PLATFORMS];
const PAGE = 3;

const sameKey = (r: PlanRow, month: string, currency: string, platform: TargetPlatform | null) =>
  r.month === month && r.currency === currency && r.platform === platform;

export const BudgetPlanSection: React.FC<Props> = ({ workspaceId, plans, currency, today, role, me, reload }) => {
  const { t } = useTranslation();
  const [pagesBack, setPagesBack] = useState(0);
  const current = monthStartOf(today);
  const months = Array.from({ length: PAGE * pagesBack + 3 }, (_, i) => shiftMonth(current, i - PAGE * pagesBack));

  const cellFor = (month: string, platform: TargetPlatform | null) => {
    const closed = nextMonthStart(month) <= today;
    const approved = plans.find((r) => r.status === 'approved' && sameKey(r, month, currency, platform)) ?? null;
    const pendingRow = plans.find((r) => r.status === 'proposed' && sameKey(r, month, currency, platform)) ?? null;
    const shown = planFor(plans, month, currency, platform);
    const edit = role === 'viewer' ? 'none' : closed ? (role === 'master' ? 'override' : 'none') : 'edit';
    const onSave = async ({ value, reason }: CellSaveInput): Promise<void> => {
      if (closed) {
        await overrideLockedPlan({ workspaceId, month, currency, platform, amount: value, reason: reason ?? '' });
      } else {
        await proposePlan({ workspaceId, month, currency, platform, amount: value, expectedVersion: approved?.id ?? null });
      }
    };
    return (
      <TargetCell
        key={platform ?? 'total'}
        testId={`plan-${month}-${platform ?? 'total'}`}
        keyLabel={`${month.slice(0, 7)} · ${keyPlatformLabel(t, platform)}`}
        kind="plan"
        value={shown?.amount ?? null}
        derived={shown?.derived ?? false}
        currency={currency}
        pending={pendingRow ? { id: pendingRow.id, value: pendingRow.amount, mine: pendingRow.proposedBy === me } : null}
        edit={edit}
        sendsForApproval={role === 'analyst'}
        canWithdraw={role !== 'viewer'}
        onSave={onSave}
        onWithdraw={(id) => withdrawProposal('plan', id)}
        reload={reload}
      >
        {shown?.mismatch && (
          <p className="mt-1 text-xs text-warning-700">
            {t('targets.plan.mismatch', {
              channelSum: money(shown.mismatch.channelSum, currency),
              total: money(shown.mismatch.total, currency),
            })}
          </p>
        )}
      </TargetCell>
    );
  };

  return (
    <section className="bg-white rounded-xl border border-neutral-200 p-6">
      <h2 className="text-sm font-semibold text-ink-900 mb-3">{t('targets.plan.heading')}</h2>
      <button type="button" className="mb-3 text-xs font-medium text-ink-900 underline"
        onClick={() => setPagesBack((n) => n + 1)}>
        {t('targets.plan.earlier')}
      </button>
      <div className="overflow-x-auto">
        <table className="min-w-full text-left">
          <thead>
            <tr className="text-xs text-neutral-700">
              <th className="px-3 py-2">{t('targets.plan.month')}</th>
              {COLUMNS.map((p) => <th key={p ?? 'total'} className="px-3 py-2">{keyPlatformLabel(t, p)}</th>)}
              <th className="px-3 py-2">{t('targets.currency')}</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month) => {
              const closed = nextMonthStart(month) <= today;
              return (
                <tr key={month} className="border-t border-neutral-100">
                  <th scope="row" className="px-3 py-2 text-sm font-medium text-ink-900 whitespace-nowrap">
                    {month.slice(0, 7)}
                    {closed && <span className="ml-1" role="img" aria-label={t('targets.plan.locked')}>🔒</span>}
                  </th>
                  {COLUMNS.map((p) => cellFor(month, p))}
                  <td className="px-3 py-2 text-sm text-neutral-700">{currency}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
};
